<!--
  YToolRow Component
  
  Manages the Y tool equipment selection and configuration for a scenario.
  Handles both uphole and downhole Y tool instances, including:
  - Y tool selection via YToolSearchView
  - Toggle state management (use_uphole_y_tool / use_downhole_y_tool)
  - Form state for bypass and equipment tubing xrefs
  - Order management for tubing arrays
  
  Delegates tubing management to YToolTubingSection components, which handle
  the display and CRUD operations for individual tubing rows. The parent
  (YToolRow) provides configuration objects (TubingConfig) that encapsulate
  all the necessary handlers and data accessors for each tubing section.
-->
<template>
  <EquipmentRow
    :key="`y-tool-${isUpper ? 'upper' : 'lower'}-${yTool?.id || 'none'}`"
    :data-cy="`y-tool-row-${isUpper ? 'upper' : 'lower'}`"
  >
    <template #left>
      <LabelledToggle
        :model-value="useYTool"
        :text="isUpper ? 'Use Upper Y Block' : 'Use Lower Y Block'"
        :data-cy="`y-tool-toggle-${isUpper ? 'upper' : 'lower'}`"
        @update:model-value="handleToggle"
      />
    </template>
    <template #default="{ toggle }">
      <EquipmentPanel
        v-if="useYTool"
        class="flex-grow-1 ml-2"
        :rule-names="[]"
        :xref-id="equipment?.id"
        :trigger-label-name="isUpper ? 'Upper Y Block' : 'Lower Y Block'"
        :data-cy="`y-tool-panel-${isUpper ? 'upper' : 'lower'}`"
        @toggle="toggle"
      >
        <template #header>
          {{ isUpper ? "Upper Y Block" : "Lower Y Block" }}
        </template>
        <template v-if="yTool" #trigger>
          <span :data-cy="`y-tool-trigger-${isUpper ? 'upper' : 'lower'}`">
            {{ computeYToolLabel(yTool) }}
          </span>
        </template>
      </EquipmentPanel>
      <!--This is a placeholder for the Y Block selection panel when it is not enabled so "DEFAULT" is not shown-->
      <span v-else></span>
    </template>
    <template #right>
      <div
        v-if="useYTool && yTool"
        class="flex align-items-center"
        style="gap: 1em"
        :data-cy="`y-tool-right-${isUpper ? 'upper' : 'lower'}`"
      >
        <ConverterInput
          label="Length"
          :model-value="yTool?.length_inches || 0"
          :base-unit="LengthUnits.Inches"
          :target-unit="getLargeLengthUnit('Length.convert.Inches')"
          :model="LargeLengthUnits.options()"
          mode="convert"
          :data-cy="`y-tool-length-${isUpper ? 'upper' : 'lower'}`"
        />
        <ConverterInput
          label="Weight"
          :model-value="yTool?.weight_pounds || 0"
          :base-unit="MassUnits.Pounds"
          :model="MassUnits.options()"
          mode="convert"
          :data-cy="`y-tool-weight-${isUpper ? 'upper' : 'lower'}`"
        />
      </div>
    </template>
    <template #expand="{ toggle }">
      <div v-if="useYTool" class="col-12 text-center">
        <YToolSearchView
          :is-admin="false"
          :data-cy="`y-tool-search-${isUpper ? 'upper' : 'lower'}`"
          @select="handleYToolSelect($event as Y_Tools, toggle)"
        />
      </div>
    </template>
  </EquipmentRow>

  <div v-if="useYTool && yTool" class="pl-6 mt-3">
    <div class="grid">
      <YToolTubingSection
        :config="bypassTubingConfig"
        :is-upper="isUpper"
        :is-metric="isMetric"
      />
      <YToolTubingSection
        :config="equipmentTubingConfig"
        :is-upper="isUpper"
        :is-metric="isMetric"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, provide } from "vue";
import { v4 } from "uuid";
import type { Y_Tools, Tubings } from "gql/api";
import { Tubings_Type_Enum } from "gql/api";
import ConverterInput from "@/components/ConverterInput.vue";
import EquipmentPanel from "@/components/scenarios/EquipmentPanel.vue";
import EquipmentRow from "@/components/scenarios/EquipmentRow.vue";
import YToolSearchView from "./YToolSearchView.vue";
import YToolTubingSection from "./YToolTubingSection.vue";
import LabelledToggle from "@/components/LabelledToggle.vue";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { MassUnits } from "@/libs/units/mass";
import { injectStrict } from "@/utils/utils";
import {
  equipmentFormKey,
  scenarioKey,
  sizingKey,
  yToolBypassTubingsFormKey,
  yToolEquipmentTubingsFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { computeYToolLabel } from "@/models/equipment/y_tool/properties";
import type {
  XrefYToolBypassTubings,
  XrefYToolEquipmentTubings,
  Form,
  InputData
} from "@/composables/forms/formTypes";

const JOURNAL_DELETE_NOW = "now()" as const;
const JOURNAL_CREATE_NOW = "now()" as const;

type TubingXref =
  | XrefYToolBypassTubings[number]
  | XrefYToolEquipmentTubings[number];

export interface TubingConfig {
  type: "bypass" | "equipment";
  title: string;
  dataCyPrefix: string;
  allowedTubingTypes: Tubings_Type_Enum[];
  order: string[];
  newTubingExists: boolean;
  getTubing: (id: string) => TubingXref | undefined;
  getTubingWeight: (tubing: TubingXref) => number;
  generateXref: () => TubingXref;
  addTubing: (index: number) => void;
  removeTubing: (tubing: TubingXref, index: number) => void;
  moveTubing: (index: number, direction: "up" | "down") => void;
  handleSelect: (
    tubing: TubingXref,
    selectedTubing: Tubings,
    toggle: () => void
  ) => void;
  handleLengthUpdate: (tubing: TubingXref, lengthFeet: number | null) => void;
  handleInitialSelect: (selectedTubing: Tubings, toggle: () => void) => void;
}

const props = defineProps<{
  isUpper: boolean;
  upholeYToolEnabled?: boolean;
}>();

const equipmentForm = injectStrict(equipmentFormKey);
const equipment = computed(() => equipmentForm.data.value);
const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey, null);
const { getLargeLengthUnit } = useScenarioUnits(scenario, sizing);

const bypassTubingsForm = injectStrict(yToolBypassTubingsFormKey);
const equipmentTubingsForm = injectStrict(yToolEquipmentTubingsFormKey);

const useYToolKey = computed(() =>
  props.isUpper ? "use_uphole_y_tool" : "use_downhole_y_tool"
);

const yTool = computed(() =>
  props.isUpper
    ? (equipment.value.r_uphole_y_tool as Y_Tools | null)
    : (equipment.value.r_downhole_y_tool as Y_Tools | null)
);

const useYTool = computed(() => equipment.value[useYToolKey.value] ?? false);

const bypassTubingOrderKey = computed(() =>
  props.isUpper
    ? "uphole_y_tool_bypass_tubing_order"
    : "downhole_y_tool_bypass_tubing_order"
);

const equipmentTubingOrderKey = computed(() =>
  props.isUpper
    ? "uphole_y_tool_equipment_tubing_order"
    : "downhole_y_tool_equipment_tubing_order"
);

const bypassTubingsOrder = computed(() => {
  const order = (equipment.value[bypassTubingOrderKey.value] as string[]) || [];
  return order.filter((tubingId: string) => {
    const tubing = bypassTubingsForm.data.value[tubingId];
    return tubing && !tubing.journal_deleted_at;
  });
});

const equipmentTubingsOrder = computed(() => {
  const order =
    (equipment.value[equipmentTubingOrderKey.value] as string[]) || [];
  return order.filter((tubingId: string) => {
    const tubing = equipmentTubingsForm.data.value[tubingId];
    return tubing && !tubing.journal_deleted_at;
  });
});

const isMetric = computed(
  () =>
    getLargeLengthUnit("Length.write.Feet").value.label ===
    LengthUnits.Meters.label
);

const newBypassTubingExists = computed(() => {
  if (bypassTubingsOrder.value?.length === 0) {
    return true;
  }
  return bypassTubingsOrder.value.some((tubingId: string) => {
    const tubing = getBypassTubing(tubingId);
    return tubing && tubing.tubing_id === null;
  });
});

const newEquipmentTubingExists = computed(() => {
  if (equipmentTubingsOrder.value?.length === 0) {
    return true;
  }
  return equipmentTubingsOrder.value.some((tubingId: string) => {
    const tubing = getEquipmentTubing(tubingId);
    return tubing && tubing.tubing_id === null;
  });
});

provide(
  "selected-equipment-id",
  computed(() => yTool.value?.id)
);

const handleToggle = (enabled: boolean) => {
  // Simply toggle the boolean field without clearing any other data
  equipmentForm.update({
    [useYToolKey.value]: enabled
  });
};

const handleYToolSelect = (selectedYTool: Y_Tools, toggle: () => void) => {
  if (props.isUpper) {
    equipmentForm.update({
      r_uphole_y_tool: selectedYTool,
      uphole_y_tool_id: selectedYTool.id
    });
  } else {
    equipmentForm.update({
      r_downhole_y_tool: selectedYTool,
      downhole_y_tool_id: selectedYTool.id
    });
  }
  toggle();
};

// Generic function to generate tubing xref
function generateTubingXref(typename: string): TubingXref {
  return {
    __typename: typename,
    id: v4(),
    scenario_equipment_id: equipment.value.id,
    tubing_id: null,
    length_feet: 0,
    parent_id: null,
    winning_revision_id: null,
    winning_revision_parent_id: null,
    winning_revision_author_id: null,
    winning_revision_created_at: JOURNAL_CREATE_NOW,
    winning_revision_updated_at: JOURNAL_CREATE_NOW,
    winning_revision_deleted_at: null,
    r_tubing: null
  } as TubingXref;
}

// Generic function to get tubing with merged data
function getTubing(id: string, form: Form<InputData>): TubingXref | undefined {
  const formData = form.data.value as Record<string, unknown>;
  const formInitialData = form.initialData.value as
    | Record<string, unknown>
    | null
    | undefined;
  const dataTubing = formData[id] as TubingXref | undefined;
  const initialTubing = formInitialData?.[id] as TubingXref | undefined;
  if (!dataTubing && !initialTubing) return undefined;
  const merged = {
    ...(initialTubing || {}),
    ...(dataTubing || {}),
    ...((initialTubing as { winning_revision_id?: string | null })
      ?.winning_revision_id
      ? {
          winning_revision_id: (
            initialTubing as { winning_revision_id: string | null }
          ).winning_revision_id
        }
      : {})
  };
  return merged as TubingXref;
}

// Generic function to calculate tubing weight
function getTubingWeight(
  tubing:
    | {
        r_tubing?: { pound_per_foot?: number } | null;
        length_feet?: number | null;
      }
    | undefined
): number {
  if (!tubing?.r_tubing) return 0;
  const lengthFeet = tubing.length_feet || 0;
  const poundPerFoot = tubing.r_tubing.pound_per_foot || 0;
  return lengthFeet * poundPerFoot;
}

function generateBypassTubingXref(): XrefYToolBypassTubings[number] {
  const typename = props.isUpper
    ? "sc_equip_xref_uphole_ytool_bypass_tubings"
    : "sc_equip_xref_downhole_ytool_bypass_tubings";
  return generateTubingXref(typename) as XrefYToolBypassTubings[number];
}

function generateEquipmentTubingXref(): XrefYToolEquipmentTubings[number] {
  const typename = props.isUpper
    ? "sc_equip_xref_uphole_ytool_equip_tubings"
    : "sc_equip_xref_downhole_ytool_equip_tubings";
  return generateTubingXref(typename) as XrefYToolEquipmentTubings[number];
}

function getBypassTubing(
  id: string
): XrefYToolBypassTubings[number] | undefined {
  return getTubing(id, bypassTubingsForm as unknown as Form<InputData>) as
    | XrefYToolBypassTubings[number]
    | undefined;
}

function getEquipmentTubing(
  id: string
): XrefYToolEquipmentTubings[number] | undefined {
  return getTubing(id, equipmentTubingsForm as unknown as Form<InputData>) as
    | XrefYToolEquipmentTubings[number]
    | undefined;
}

function getBypassTubingWeight(
  tubing: XrefYToolBypassTubings[number] | undefined
): number {
  return getTubingWeight(tubing);
}

function getEquipmentTubingWeight(
  tubing: XrefYToolEquipmentTubings[number] | undefined
): number {
  return getTubingWeight(tubing);
}

interface TubingOperationConfig {
  generateXref: () => TubingXref;
  form: Form<InputData>;
  order: string[];
  orderKey: string;
}

// Generic function to add tubing
function addTubing(index: number, config: TubingOperationConfig): void {
  const tubing = config.generateXref();
  const tubingIndex = index < 0 ? 0 : index;
  const newOrder = [...config.order];
  newOrder.splice(tubingIndex, 0, tubing.id);

  config.form.update({
    [tubing.id as string]: tubing
  } as Partial<InputData>);
  equipmentForm.update({
    [config.orderKey]: newOrder
  });
}

// Generic function to remove tubing
function removeTubing(
  tubing: TubingXref,
  index: number,
  config: TubingOperationConfig
): void {
  const newOrder = [...config.order];
  newOrder.splice(index, 1);

  const formInitialData = config.form.initialData.value as
    | Record<string, unknown>
    | null
    | undefined;
  if (formInitialData?.[tubing.id as string]) {
    const initialTubing = formInitialData[tubing.id as string] as TubingXref & {
      winning_revision_id?: string | null;
    };
    config.form.update({
      [tubing.id as string]: {
        ...tubing,
        journal_deleted_at: JOURNAL_DELETE_NOW,
        ...(initialTubing?.winning_revision_id
          ? { winning_revision_id: initialTubing.winning_revision_id }
          : {})
      }
    } as Partial<InputData>);
  } else {
    const formData = config.form.data.value as Record<string, unknown>;
    delete formData[tubing.id as string];
  }

  equipmentForm.update({
    [config.orderKey]: newOrder
  });
}

// Generic function to move tubing up or down
function moveTubing(
  index: number,
  direction: "up" | "down",
  config: TubingOperationConfig
): void {
  const newOrder = [...config.order];
  const newIndex = direction === "up" ? index - 1 : index + 1;

  // Validate bounds
  if (newIndex < 0 || newIndex >= newOrder.length) {
    return;
  }

  // Swap items
  [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];

  equipmentForm.update({
    [config.orderKey]: newOrder
  });
}

// Generic function to update tubing
function updateTubing(
  tubing: TubingXref,
  index: number,
  config: TubingOperationConfig
): void {
  const newOrder = [...config.order];
  if (newOrder[index] !== tubing.id) {
    newOrder[index] = tubing.id;
  }

  const formInitialData = config.form.initialData.value as
    | Record<string, unknown>
    | null
    | undefined;
  const initialTubing = formInitialData?.[tubing.id as string] as
    | (TubingXref & { winning_revision_id?: string | null })
    | undefined;
  const updatedTubing = {
    ...tubing,
    ...(initialTubing?.winning_revision_id
      ? { winning_revision_id: initialTubing.winning_revision_id }
      : {})
  };

  config.form.update({
    [tubing.id as string]: updatedTubing
  } as Partial<InputData>);
  equipmentForm.update({
    [config.orderKey]: newOrder
  });
}

function addBypassTubing(index: number): void {
  addTubing(index, {
    generateXref: generateBypassTubingXref,
    form: bypassTubingsForm as unknown as Form<InputData>,
    order: bypassTubingsOrder.value,
    orderKey: bypassTubingOrderKey.value
  });
}

function addEquipmentTubing(index: number): void {
  addTubing(index, {
    generateXref: generateEquipmentTubingXref,
    form: equipmentTubingsForm as unknown as Form<InputData>,
    order: equipmentTubingsOrder.value,
    orderKey: equipmentTubingOrderKey.value
  });
}

function removeBypassTubing(
  tubing: XrefYToolBypassTubings[number],
  index: number
): void {
  removeTubing(tubing, index, {
    generateXref: generateBypassTubingXref,
    form: bypassTubingsForm as unknown as Form<InputData>,
    order: bypassTubingsOrder.value,
    orderKey: bypassTubingOrderKey.value
  });
}

function removeEquipmentTubing(
  tubing: XrefYToolEquipmentTubings[number],
  index: number
): void {
  removeTubing(tubing, index, {
    generateXref: generateEquipmentTubingXref,
    form: equipmentTubingsForm as unknown as Form<InputData>,
    order: equipmentTubingsOrder.value,
    orderKey: equipmentTubingOrderKey.value
  });
}

function moveBypassTubing(index: number, direction: "up" | "down"): void {
  moveTubing(index, direction, {
    generateXref: generateBypassTubingXref,
    form: bypassTubingsForm as unknown as Form<InputData>,
    order: bypassTubingsOrder.value,
    orderKey: bypassTubingOrderKey.value
  });
}

function moveEquipmentTubing(index: number, direction: "up" | "down"): void {
  moveTubing(index, direction, {
    generateXref: generateEquipmentTubingXref,
    form: equipmentTubingsForm as unknown as Form<InputData>,
    order: equipmentTubingsOrder.value,
    orderKey: equipmentTubingOrderKey.value
  });
}

function updateBypassTubing(
  tubing: XrefYToolBypassTubings[number],
  index: number
): void {
  updateTubing(tubing, index, {
    generateXref: generateBypassTubingXref,
    form: bypassTubingsForm as unknown as Form<InputData>,
    order: bypassTubingsOrder.value,
    orderKey: bypassTubingOrderKey.value
  });
}

function updateEquipmentTubing(
  tubing: XrefYToolEquipmentTubings[number],
  index: number
): void {
  updateTubing(tubing, index, {
    generateXref: generateEquipmentTubingXref,
    form: equipmentTubingsForm as unknown as Form<InputData>,
    order: equipmentTubingsOrder.value,
    orderKey: equipmentTubingOrderKey.value
  });
}

interface TubingHandlerConfig {
  updateTubing: (tubing: TubingXref, index: number) => void;
  order: string[];
}

function handleTubingSelect(
  tubing: TubingXref,
  selectedTubing: Tubings,
  toggle: () => void,
  config: TubingHandlerConfig
): void {
  toggle();
  config.updateTubing(
    {
      ...tubing,
      tubing_id: selectedTubing.id,
      r_tubing: selectedTubing,
      length_feet: tubing.length_feet || 0
    },
    config.order.indexOf(tubing.id)
  );
}

function handleTubingLengthUpdate(
  tubing: TubingXref,
  lengthFeet: number | null,
  config: TubingHandlerConfig
): void {
  config.updateTubing(
    {
      ...tubing,
      length_feet: lengthFeet || 0
    },
    config.order.indexOf(tubing.id)
  );
}

interface InitialTubingSelectConfig extends TubingOperationConfig {
  updateTubing: (tubing: TubingXref, index: number) => void;
}

function handleInitialTubingSelect(
  selectedTubing: Tubings,
  toggle: () => void,
  config: InitialTubingSelectConfig
): void {
  toggle();
  const tubing = config.generateXref();
  const newTubing = {
    ...tubing,
    tubing_id: selectedTubing.id,
    r_tubing: selectedTubing,
    length_feet: 0
  };
  config.form.update({
    [newTubing.id as string]: newTubing
  } as Partial<InputData>);
  if (config.order.length === 0) {
    equipmentForm.update({
      [config.orderKey]: [newTubing.id]
    });
  } else {
    config.updateTubing(newTubing, 0);
  }
}

function handleBypassTubingSelect(
  tubing: XrefYToolBypassTubings[number],
  selectedTubing: Tubings,
  toggle: () => void
): void {
  handleTubingSelect(tubing, selectedTubing, toggle, {
    updateTubing: updateBypassTubing as (
      tubing: TubingXref,
      index: number
    ) => void,
    order: bypassTubingsOrder.value
  });
}

function handleEquipmentTubingSelect(
  tubing: XrefYToolEquipmentTubings[number],
  selectedTubing: Tubings,
  toggle: () => void
): void {
  handleTubingSelect(tubing, selectedTubing, toggle, {
    updateTubing: updateEquipmentTubing as (
      tubing: TubingXref,
      index: number
    ) => void,
    order: equipmentTubingsOrder.value
  });
}

function handleBypassTubingLengthUpdate(
  tubing: XrefYToolBypassTubings[number],
  lengthFeet: number | null
): void {
  handleTubingLengthUpdate(tubing, lengthFeet, {
    updateTubing: updateBypassTubing as (
      tubing: TubingXref,
      index: number
    ) => void,
    order: bypassTubingsOrder.value
  });
}

function handleEquipmentTubingLengthUpdate(
  tubing: XrefYToolEquipmentTubings[number],
  lengthFeet: number | null
): void {
  handleTubingLengthUpdate(tubing, lengthFeet, {
    updateTubing: updateEquipmentTubing as (
      tubing: TubingXref,
      index: number
    ) => void,
    order: equipmentTubingsOrder.value
  });
}

function handleInitialBypassTubingSelect(
  selectedTubing: Tubings,
  toggle: () => void
): void {
  handleInitialTubingSelect(selectedTubing, toggle, {
    generateXref: generateBypassTubingXref,
    form: bypassTubingsForm as unknown as Form<InputData>,
    order: bypassTubingsOrder.value,
    orderKey: bypassTubingOrderKey.value,
    updateTubing: updateBypassTubing as (
      tubing: TubingXref,
      index: number
    ) => void
  });
}

function handleInitialEquipmentTubingSelect(
  selectedTubing: Tubings,
  toggle: () => void
): void {
  handleInitialTubingSelect(selectedTubing, toggle, {
    generateXref: generateEquipmentTubingXref,
    form: equipmentTubingsForm as unknown as Form<InputData>,
    order: equipmentTubingsOrder.value,
    orderKey: equipmentTubingOrderKey.value,
    updateTubing: updateEquipmentTubing as (
      tubing: TubingXref,
      index: number
    ) => void
  });
}

// Configuration objects for TubingSection component
// Using type assertions to handle union type compatibility
const bypassTubingConfig: TubingConfig = {
  type: "bypass" as const,
  title: "Bypass Tubing",
  dataCyPrefix: "y-tool-bypass-tubing",
  allowedTubingTypes: [Tubings_Type_Enum.Bypass, Tubings_Type_Enum.Both],
  get order() {
    return bypassTubingsOrder.value;
  },
  get newTubingExists() {
    return newBypassTubingExists.value;
  },
  getTubing: getBypassTubing,
  getTubingWeight: getBypassTubingWeight as (tubing: TubingXref) => number,
  generateXref: generateBypassTubingXref,
  addTubing: addBypassTubing,
  removeTubing: removeBypassTubing as (
    tubing: TubingXref,
    index: number
  ) => void,
  moveTubing: moveBypassTubing,
  handleSelect: handleBypassTubingSelect as (
    tubing: TubingXref,
    selectedTubing: Tubings,
    toggle: () => void
  ) => void,
  handleLengthUpdate: handleBypassTubingLengthUpdate as (
    tubing: TubingXref,
    lengthFeet: number | null
  ) => void,
  handleInitialSelect: handleInitialBypassTubingSelect
};

const equipmentTubingConfig: TubingConfig = {
  type: "equipment" as const,
  title: "Equipment Tubing",
  dataCyPrefix: "y-tool-equipment-tubing",
  allowedTubingTypes: [Tubings_Type_Enum.Normal, Tubings_Type_Enum.Both],
  get order() {
    return equipmentTubingsOrder.value;
  },
  get newTubingExists() {
    return newEquipmentTubingExists.value;
  },
  getTubing: getEquipmentTubing,
  getTubingWeight: getEquipmentTubingWeight as (tubing: TubingXref) => number,
  generateXref: generateEquipmentTubingXref,
  addTubing: addEquipmentTubing,
  removeTubing: removeEquipmentTubing as (
    tubing: TubingXref,
    index: number
  ) => void,
  moveTubing: moveEquipmentTubing,
  handleSelect: handleEquipmentTubingSelect as (
    tubing: TubingXref,
    selectedTubing: Tubings,
    toggle: () => void
  ) => void,
  handleLengthUpdate: handleEquipmentTubingLengthUpdate as (
    tubing: TubingXref,
    lengthFeet: number | null
  ) => void,
  handleInitialSelect: handleInitialEquipmentTubingSelect
};
</script>
